import Box from '@mui/material/Box';
import React from 'react';

import { LabeledDetail } from '@/components/molecules/LabeledDetail';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { Amount } from '@/types/amount';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';
import { FeeDetails, FeeType, FixedFee, PercentageFee } from '@/types/transaction';
import { formatAmount, FormatStyle } from '@/utility/formatAmount';

interface Props {
  readonly feeDetails: FeeDetails;
  readonly totalAmount: Amount;
  readonly fromCurrency: CurrencyCodeType;
}

const PERCENT_FORMATTER_CONFIG = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  style: 'percent' as const,
};

interface FeeElement {
  readonly label: readonly string[];
  readonly currency: CurrencyCodeType;
  readonly value: number | string;
}

export const DetailsDrawerFees: React.FC<Props> = (props: Props): React.ReactElement => {
  const { feeDetails, fromCurrency } = props;

  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();
  const percentFormatter = useLocalizedFormatter(PERCENT_FORMATTER_CONFIG);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();

  const feeItems = React.useMemo((): readonly FeeElement[] => {
    const { fixedFees, percentageFees } = feeDetails;
    const percentageFeesWithoutTaxes = percentageFees.filter(
      (fee: PercentageFee): boolean => fee.type !== FeeType.TAX,
    );
    const taxes = percentageFees.filter((fee: PercentageFee): boolean => fee.type === FeeType.TAX);

    return [
      ...(percentageFeesWithoutTaxes.length > 0
        ? percentageFeesWithoutTaxes.map(
            (fee: PercentageFee): FeeElement => ({
              label: [
                'TRANSFER_FEES_WITH_PERCENTAGE',
                `${percentFormatter.format(Number(fee.percentageRate) / 100)}`,
              ],
              currency: fee.feeCurrency,
              value: fee.calculatedFee,
            }),
          )
        : [
            {
              label: ['TRANSFER_FEES_FREE'],
              currency: fromCurrency,
              value: 0,
            },
          ]),
      ...fixedFees.map(
        (fee: FixedFee): FeeElement => ({
          label: ['PAYMENT_RAIL_FEE', fee.paymentRail],
          currency: fee.currencyCode,
          value: fee.value,
        }),
      ),
      ...(taxes.length > 0
        ? taxes.map(
            (tax: PercentageFee): FeeElement =>
              tax.feeCurrency === CurrencyCodeType.BRL
                ? {
                    label: [
                      'IOF_WITH_PERCENTAGE',
                      `${percentFormatter.format(Number(tax.percentageRate) / 100)}`,
                    ],
                    currency: tax.feeCurrency,
                    value: tax.calculatedFee,
                  }
                : {
                    label: ['TAX', `${percentFormatter.format(Number(tax.percentageRate) / 100)}`],
                    currency: tax.feeCurrency,
                    value: tax.calculatedFee,
                  },
          )
        : []),
    ].filter((item: FeeElement): item is FeeElement => item !== null);
  }, [feeDetails, fromCurrency, percentFormatter]);

  return (
    <Box>
      {feeItems.map((item: FeeElement): React.ReactElement => {
        const { label, currency, value } = item;
        const [untranslatedLabel, ...labelParameters] = label;

        const numeric = typeof value === 'number' ? value : Number(value);
        const formattedValue = isNaN(numeric)
          ? ''
          : formatAmount(locale, numeric, currency, FormatStyle.short, 2, displayUsdcAsUsd);
        const resolvedLabel = tr(untranslatedLabel, ...labelParameters);
        const key = label.join('');

        return <LabeledDetail key={key} label={resolvedLabel} value={formattedValue} />;
      })}
    </Box>
  );
};
